// src/pages/StrategicObjectivesPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  alpha,
  useTheme
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  GpsFixed as GpsFixedIcon,
  EditNote as EditNoteIcon
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

import DashboardLayout from '../components/DashboardLayout';

// ATENÇÃO: A importação de 'useAuth' foi movida para onde é realmente usada.
import { useAuth } from '../context/AuthContext';

import {
  listObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  getDirectionForType, 
  saveDirectionForType
} from '../api/strategicObjectivesApi';

const TYPE_STYLE = {
  financeiro: { key: 'financeiro', label: 'Financeiro', color: '#16a34a' },
  mercadoecliente: { key: 'mercadoecliente', label: 'Mercado & Cliente', color: '#2563eb' },
  processos: { key: 'processos', label: 'Processos', color: '#111827' },
  aprendizadoedesenvolvimento: {
    key: 'aprendizadoedesenvolvimento',
    label: 'Aprendizado & Desenvolvimento',
    color: '#dc2626'
  }
};

const TYPES = Object.values(TYPE_STYLE);

function ObjectiveForm({
  open,
  onClose,
  initial,
  tipo,
  accent
}) {
  const [formData, setFormData] = useState({ titulo: '', descricao: '', ordem: '' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setFormData({
        titulo: initial?.titulo || '',
        descricao: initial?.descricao || '',
        ordem: initial?.ordem || ''
      });
    }
  }, [open, initial]);
  
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.titulo.trim()) {
      setSnackbar({ open: true, message: 'O título é obrigatório.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo,
        titulo: formData.titulo.trim(),
        descricao: formData.descricao.trim() || null,
        ordem: formData.ordem ? Number(formData.ordem) : null,
        ativo: true
      };
      if (isEdit) {
        await updateObjective(initial.id, payload);
      } else {
        await createObjective(payload);
      }
      onClose(true);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Falha ao salvar.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ p: 2, backgroundColor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: accent, fontWeight: 700 }}>
                    <GpsFixedIcon />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'inherit' }}>
                        {isEdit ? 'Editar Objetivo' : 'Novo Objetivo'}
                    </Typography>
                </Box>
                <IconButton onClick={() => onClose(false)} aria-label="fechar">
                    <CloseIcon />
                </IconButton>
            </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField name="titulo" label="Título do Objetivo" variant="filled" fullWidth value={formData.titulo} onChange={handleChange} helperText="Seja claro e conciso ao nomear o objetivo." disabled={saving}/>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField name="ordem" label="Ordem (Opcional)" type="number" variant="filled" fullWidth value={formData.ordem} onChange={handleChange} helperText="De 1 a 10 para priorizar." inputProps={{ min: 1, max: 10 }} disabled={saving} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="descricao" label="Descrição (Opcional)" variant="filled" fullWidth multiline rows={3} value={formData.descricao} onChange={handleChange} helperText="Detalhe o que este objetivo representa." disabled={saving} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" onClick={() => onClose(false)} disabled={saving}>
            Cancelar
          </Button>
          <LoadingButton loading={saving} variant="contained" onClick={handleSave} startIcon={<SaveIcon />} sx={{ bgcolor: accent, color: 'white', '&:hover': { bgcolor: alpha(accent, 0.9) } }}>
            {isEdit ? 'Salvar Alterações' : 'Criar Objetivo'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const StrategicObjectivesPage = () => {
  const navigate = useNavigate();
  // Hooks 'useTheme' e 'useAuth' são usados aqui, no componente visual.
  const theme = useTheme(); 
  const auth = useAuth?.() || {};
  
  const [tab, setTab] = useState(0);
  const currentType = TYPES[tab].key;
  const accentColor = useMemo(() => TYPE_STYLE[currentType].color, [currentType]);
  
  const [objectives, setObjectives] = useState([]);
  const [strategicDirection, setStrategicDirection] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [isSavingDirection, setIsSavingDirection] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formOpen, setFormOpen] = useState(false);
  const [objectiveBeingEdited, setObjectiveBeingEdited] = useState(null);

  const canCreateMore = objectives.length < 10;
  
  const loadDataForCurrentType = useCallback(async () => {
    setLoading(true);
    try {
      const [objectivesData, directionData] = await Promise.all([
        listObjectives(currentType),
        getDirectionForType(currentType) 
      ]);
      setObjectives(objectivesData);
      setStrategicDirection(directionData?.text || '');
    } catch (error) {
      if (String(error.message || '').includes('401')) {
        navigate('/login');
        return;
      }
      setSnackbar({ open: true, message: `Erro ao carregar dados: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentType, navigate]);

  useEffect(() => {
    loadDataForCurrentType();
  }, [loadDataForCurrentType]);

  const handleSaveDirection = async () => {
    setIsSavingDirection(true);
    try {
        await saveDirectionForType(currentType, strategicDirection);
        setSnackbar({ open: true, message: 'Direcionamento Estratégico salvo com sucesso!', severity: 'success' });
    } catch (error) {
        setSnackbar({ open: true, message: `Falha ao salvar: ${error.message}`, severity: 'error' });
    } finally {
        setIsSavingDirection(false);
    }
  };

  const handleCreate = () => {
    setObjectiveBeingEdited(null);
    setFormOpen(true);
  };

  const handleEdit = (objective) => {
    setObjectiveBeingEdited(objective);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este objetivo?')) return;
    try {
      await deleteObjective(id);
      await loadDataForCurrentType();
    } catch (error) {
      setSnackbar({ open: true, message: `Falha ao excluir: ${error.message}`, severity: 'error' });
    }
  };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const headerBackground = alpha(accentColor, 0.10);
  const borderColor = alpha(accentColor, 0.35);

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
            Voltar para o Dashboard
        </Button>
        
        <Paper elevation={0} sx={{ mb: 3, p: 3, borderRadius: 3, border: `1px solid ${alpha(accentColor, 0.25)}`, background: `linear-gradient(180deg, ${headerBackground} 0%, ${alpha(accentColor, 0.03)} 100%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: accentColor, boxShadow: `0 0 0 4px ${alpha(accentColor, 0.12)}` }}/>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>Objetivos Estratégicos</Typography>
          </Box>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Defina um direcionamento para cada perspectiva e gerencie os objetivos relacionados.
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} variant="scrollable" scrollButtons="auto" textColor="inherit" TabIndicatorProps={{ style: { backgroundColor: accentColor, height: 3, borderRadius: 3 } }} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 } }}>
            {TYPES.map((typeDefinition, index) => (
              <Tab key={typeDefinition.key} label={ <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: typeDefinition.color }}/> <span>{typeDefinition.label}</span> </Box> } sx={{ color: tab === index ? typeDefinition.color : 'text.secondary', opacity: 1 }}/>
            ))}
          </Tabs>
        </Paper>
        
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${alpha(accentColor, 0.25)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <EditNoteIcon sx={{ color: accentColor }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Direcionamento Estratégico da Perspectiva</Typography>
            </Box>
            <TextField
                label={`Direcionamento para ${TYPE_STYLE[currentType].label}`}
                variant="filled" fullWidth multiline rows={4}
                value={strategicDirection}
                onChange={(e) => setStrategicDirection(e.target.value)}
                placeholder="Defina aqui a principal diretriz para esta perspectiva..."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <LoadingButton
                    loading={isSavingDirection}
                    variant="contained"
                    onClick={handleSaveDirection}
                    startIcon={<SaveIcon />}
                    sx={{ bgcolor: accentColor, color: 'white', '&:hover': { bgcolor: alpha(accentColor, 0.9) } }}
                >
                    Salvar Direcionamento
                </LoadingButton>
            </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Objetivos de {TYPE_STYLE[currentType].label}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
              ({objectives.length}/10)
            </Typography>
          </Typography>
          <Tooltip title={canCreateMore ? 'Novo objetivo' : 'Limite de 10 atingido'}>
            <span><Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} disabled={!canCreateMore} sx={{ bgcolor: accentColor, color: 'white', '&:hover': { bgcolor: alpha(accentColor, 0.9) }, borderRadius: 2 }}>Novo Objetivo</Button></span>
          </Tooltip>
        </Box>

        {!canCreateMore && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>Limite de 10 objetivos para este tipo alcançado.</Alert>}

        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(accentColor, 0.25)}` }}>
          {loading ? ( <Box sx={{ p: 3 }}><Typography>Carregando…</Typography></Box> ) 
          : objectives.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Nenhum objetivo cadastrado</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Comece criando o primeiro objetivo para <b>{TYPE_STYLE[currentType].label}</b>.</Typography>
              <Button onClick={handleCreate} startIcon={<AddIcon />} variant="contained" sx={{ bgcolor: accentColor, color: 'white', '&:hover': { bgcolor: alpha(accentColor, 0.9) } }}>Criar Objetivo</Button>
            </Box>
          ) : (
            <Box>
              {objectives.map((objective, index) => (
                  <Box key={objective.id}>
                    <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start', borderLeft: `4px solid ${accentColor}`, ':hover': { backgroundColor: alpha(accentColor, 0.04) } }}>
                      <Chip label={`#${objective.ordem || index + 1}`} size="small" sx={{ fontWeight: 700, backgroundColor: accentColor, color: 'white' }}/>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, display: 'inline-block', color: 'white', backgroundColor: accentColor, px: 1, py: 0.25, borderRadius: 1 }}>{objective.titulo}</Typography>
                        {objective.descricao && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{objective.descricao}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Editar"><IconButton onClick={() => handleEdit(objective)}><EditIcon /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton color="error" onClick={() => handleDelete(objective.id)}><DeleteIcon /></IconButton></Tooltip>
                      </Box>
                    </Box>
                    {index < objectives.length - 1 && <Divider sx={{ ml: '4px' }} />}
                  </Box>
              ))}
            </Box>
          )}
        </Paper>

        <ObjectiveForm open={formOpen} onClose={async (changed) => { setFormOpen(false); setObjectiveBeingEdited(null); if (changed) await loadDataForCurrentType(); }} initial={objectiveBeingEdited} tipo={currentType} accent={accentColor} />
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </DashboardLayout>
  );
};

export default StrategicObjectivesPage;